import * as React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar as MuiAvatar, 
  IconButton, 
  styled, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Modal,
  Card,
  CardContent,
  Chip,
  Grid
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { deepPurple, deepOrange, grey } from '@mui/material/colors';
import { TeamSeason, LeagueSeason, Division, PublicUserInfo } from '../../../sdk/model';
import { useNavigate } from 'react-router-dom';
import { useTeamOperations } from '../../../hooks/useTeamOperations';
import { useSnackbar } from 'notistack';
import { useAppSelector } from '../../../app/hooks';
import UserInfoCard from '../../cards/userCard'

// Filter out undefined team seasons and ensure required properties exist
const getValidTeamSeasons = (teams: (TeamSeason | undefined)[]): TeamSeason[] => {
  return teams.filter((team): team is TeamSeason => 
    !!team && 
    team.teamName !== undefined && 
    team.teamId !== undefined
  );
};

// Extend the Division type to include description
interface DivisionWithDescription extends Division {
  description?: string;
}

interface DivisionStandingsProps {
  leagueSeason: LeagueSeason;
  teamSeasons: TeamSeason[];
  leagueId: string;
  joinEnabled?: boolean;
  onJoinSuccess?: () => void;
  waiverPriorityOrder: string[];
}

const DivisionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: theme.spacing(3, 0, 2),
  paddingBottom: theme.spacing(1),
  borderBottom: `2px solid ${theme.palette.divider}`,
}));

const DivisionLeaderBadge = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: theme.palette.success.contrastText,
  fontSize: '0.7rem',
  fontWeight: 'bold',
  padding: '2px 8px',
  borderRadius: '12px',
  marginLeft: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const DivisionStandings: React.FC<DivisionStandingsProps> = ({
  leagueSeason,
  teamSeasons,
  leagueId,
  joinEnabled = false,
  onJoinSuccess,
  waiverPriorityOrder,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useAppSelector((state) => state.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewingImage, setViewingImage] = React.useState<string | null>(null);
  const { joinTeam, isJoining } = useTeamOperations({
    currentUserId: currentUser?.id,
    onSuccess: () => {
      enqueueSnackbar('Successfully joined the team!', { variant: 'success' });
      onJoinSuccess?.();
    }
  });
  const [viewingUser, setViewingUser] = React.useState<User | undefined>(undefined);

  const handleJoinTeam = async (e: React.MouseEvent<HTMLButtonElement>, teamId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      enqueueSnackbar('You must be logged in to join a team', { variant: 'error' });
      return;
    }

    try {
      await joinTeam(teamId);
      enqueueSnackbar('Successfully joined the team!', { variant: 'success' });
      onJoinSuccess?.();
    } catch (error) {
      console.error('Error joining team:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to join team', { variant: 'error' });
    }
  };
  const [sortConfig, setSortConfig] = React.useState<{ key: keyof TeamSeason; direction: 'asc' | 'desc' }>({
    key: 'divisionRank',
    direction: 'asc',
  });
  
  if (!leagueSeason.divisions?.length) {
    return null;
  }
  
  const teamSeasonMap = new Map(teamSeasons.map(teamSeason => [teamSeason.teamId, teamSeason]));
  const divisionColors = [deepPurple[300], deepOrange[300], grey[500], '#2e7d32', '#1565c0'];
  
  const handleSort = (key: keyof TeamSeason) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  
  const sortedTeams = (teams: (TeamSeason | undefined)[]): TeamSeason[] => {
    return getValidTeamSeasons(teams).sort((a, b) => {
      // Only sort by keys that we know exist on TeamSeason
      const sortKey = sortConfig.key as keyof TeamSeason;
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      
      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
      
      // Compare values based on sort direction
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  const navigateToTeam = (teamId?: string) => {
    if (teamId) {
      navigate(`/league/${leagueId}/team/${teamId}`);
    }
  };

  // Mobile card component for individual teams
  const TeamCard: React.FC<{ teamSeason: TeamSeason; divisionColor: string; rank: number }> = ({ 
    teamSeason, 
    divisionColor, 
    rank 
  }) => {
    const isOwner = teamSeason.owners?.some(owner => owner.id === currentUser?.id);
    
    return (
      <Card 
        sx={{ 
          mb: 1,
          border: isOwner ? `2px solid ${theme.palette.primary.main}` : '1px solid',
          borderColor: isOwner ? theme.palette.primary.main : 'divider',
          borderLeft: `4px solid ${divisionColor}`,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Chip 
                label={`#${rank}`} 
                size="small" 
                sx={{ bgcolor: divisionColor, color: 'white', fontWeight: 'bold', minWidth: 32 }}
              />
              {teamSeason.teamPicture && (
                <Avatar 
                  src={teamSeason.teamPicture} 
                  sx={{ width: 32, height: 32 }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Button 
                  onClick={() => navigateToTeam(teamSeason.teamId)}
                  sx={{ p: 0, justifyContent: 'flex-start', minWidth: 'auto' }}
                >
                  <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'left' }}>
                    {teamSeason.teamName || 'Unnamed Team'}
                  </Typography>
                </Button>
                {isOwner && (
                  <Chip 
                    label="YOU" 
                    size="small" 
                    color="primary" 
                    sx={{ mt: 0.5, fontSize: '0.6rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Record</Typography>
              <Typography variant="body2" fontWeight="medium">
                {teamSeason.wins}-{teamSeason.losses}
                {teamSeason.ties ? `-${teamSeason.ties}` : ''}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">PF</Typography>
              <Typography variant="body2" fontWeight="medium">
                {('pointsScored' in teamSeason && typeof teamSeason.pointsScored === 'number') 
                  ? teamSeason.pointsScored?.toFixed(1) : '0.0'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">PA</Typography>
              <Typography variant="body2" fontWeight="medium">
                {('pointsAgainst' in teamSeason && typeof teamSeason.pointsAgainst === 'number') 
                  ? teamSeason.pointsAgainst.toFixed(1) : '0.0'}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {teamSeason.owners?.[0] ? teamSeason.owners.map(owner => (
                <Button 
                  key={owner.id}
                  onClick={() => setViewingUser(teamSeason.owners?.[0])}
                  sx={{ p: 0, minWidth: 'auto' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {'username' in owner ? (owner as any).username : 'Owner'}
                    </Typography>
                    {'picture' in owner && (owner as any).picture && (
                      <Avatar 
                        src={(owner as any).picture} 
                        sx={{ width: 20, height: 20 }}
                      />
                    )}
                  </Box>
                </Button>
              )) : joinEnabled ? (
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => 
                    teamSeason.teamId && handleJoinTeam(e, teamSeason.teamId)
                  }
                  disabled={isJoining}
                  sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                >
                  {isJoining ? 'Joining...' : 'Join'}
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No Owner
                </Typography>
              )}
            </Box>
            
            <Chip 
              label={`W${waiverPriorityOrder ? waiverPriorityOrder.indexOf(teamSeason.teamId!) + 1 : ''}`} 
              size="small" 
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      <Modal open={!!viewingUser} onClose={() => setViewingUser(undefined)} 
          aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
          sx={{margin: '5%'}}>
        <UserInfoCard userId={viewingUser?.id!} callback={() => setViewingUser(undefined)} />
      </Modal>
      <Dialog open={!!viewingImage} maxWidth='md' fullWidth>
        <DialogContent>
          <Box position="absolute" top={8} right={8} zIndex={1}>
            <IconButton onClick={() => setViewingImage(null)}>
              <Close />
            </IconButton>
          </Box>
          <Box component="img" src={viewingImage} sx={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }} />
        </DialogContent>
      </Dialog>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <EmojiEventsIcon color="warning" sx={{ mr: 1 }} />
        Division Standings
      </Typography>
      
      {leagueSeason.divisions?.map((division, divIndex) => {
        const divisionTeams = division.teams
          ?.map(teamId => teamSeasonMap.get(teamId))
          .filter(Boolean);
          
        if (!divisionTeams?.length) return null;
        
        const divisionColor = divisionColors[divIndex % divisionColors.length];
        const sortedDivisionTeams = sortedTeams(divisionTeams);
        const divisionLeader = sortedDivisionTeams[0];
        const divisionWithDesc = division as DivisionWithDescription;
        
        return (
          <Box 
            key={division.name || divIndex} 
            sx={{ 
              mb: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box 
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 2,
                flexDirection: isMobile ? 'column' : 'row'
              }}
            >
              <Box sx={{ 
                width: 4, 
                height: isMobile ? 20 : 40, 
                bgcolor: divisionColor,
                borderRadius: 1,
                mr: isMobile ? 0 : 1,
                alignSelf: isMobile ? 'stretch' : 'auto'
              }} />
              
              <Box sx={{ width: '100%' }}>
                {isMobile ? (
                  // Mobile Division Header Layout
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <IconButton 
                        onClick={() => setViewingImage(division.picture)}
                        disabled={!division.picture}
                        sx={{ p: 0, width: 50, height: 50 }}
                      >
                        <Avatar 
                          sx={{ width: 50, height: 50 }} 
                          alt={division.name} 
                          src={division.picture}
                        />
                      </IconButton>
                      <Typography 
                        variant="h6" 
                        component="div" 
                        sx={{ 
                          fontWeight: 600,
                          flex: 1,
                          wordBreak: 'break-word'
                        }}
                      >
                        {division.name}
                      </Typography>
                    </Box>
                    
                    {divisionLeader && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={`Leader: ${divisionLeader.teamName}`}
                          size="small"
                          sx={{
                            bgcolor: theme.palette.success.main,
                            color: theme.palette.success.contrastText,
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                      </Box>
                    )}
                    
                    {divisionWithDesc.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          wordBreak: 'break-word',
                          hyphens: 'auto'
                        }}
                      >
                        {divisionWithDesc.description}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  // Desktop Division Header Layout
                  <Box>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}
                    >
                      <IconButton 
                        onClick={() => setViewingImage(division.picture)}
                        disabled={!division.picture}
                        sx={{ p: 0, width: 80, height: 80 }}
                      >
                        <Avatar 
                          sx={{ display: 'inline-block' }} 
                          alt={division.name} 
                          src={division.picture}
                        />
                      </IconButton>
                      {division.name}
                      {divisionLeader && (
                        <DivisionLeaderBadge>
                          Leader: {divisionLeader.teamName}
                        </DivisionLeaderBadge>
                      )}
                    </Typography>
                    {divisionWithDesc.description && (
                      <Typography variant="body2" color="text.secondary">
                        {divisionWithDesc.description}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            
            {isMobile ? (
              // Mobile Card Layout
              <Box sx={{ p: 2 }}>
                {sortedDivisionTeams.map((teamSeason, index) => (
                  <TeamCard
                    key={teamSeason.teamId}
                    teamSeason={teamSeason}
                    divisionColor={divisionColor}
                    rank={teamSeason.divisionRank || index + 1}
                  />
                ))}
              </Box>
            ) : (
              // Desktop Table Layout
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ 
                      '& th': { 
                        fontWeight: 'bold',
                        backgroundColor: divisionColor
                      } 
                    }}>
                      <TableCell>Rank</TableCell>
                      <TableCell>Team</TableCell>
                      <TableCell align="right">Record</TableCell>
                      <TableCell align="right">PF</TableCell>
                      <TableCell align="right">PA</TableCell>
                      <TableCell align="right">Owner(s)</TableCell>
                      <TableCell align="right">Waiver</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedDivisionTeams.map((teamSeason, index) => {
                      const isOwner = teamSeason.owners?.some(owner => owner.id === currentUser?.id);
                      
                      return (
                        <TableRow 
                          key={teamSeason.teamId}
                          sx={{ 
                            ...(teamSeason.owners?.some(owner => owner.id === currentUser?.id) && {
                              borderLeft: `4px solid ${theme.palette.primary.main}`,
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? 'rgba(25, 118, 210, 0.15)' 
                                  : 'rgba(25, 118, 210, 0.08)'
                              }
                            })
                          }}
                        >
                          <TableCell>{teamSeason.divisionRank || 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {teamSeason.teamPicture && (
                                <Avatar 
                                  src={teamSeason.teamPicture} 
                                  sx={{ width: 30, height: 30 }}
                                />
                              )}
                              <Box>
                                <Button onClick={() => navigateToTeam(teamSeason.teamId)}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {teamSeason.teamName || 'Unnamed Team'}
                                    </Typography>
                                    {teamSeason.owners?.some(owner => owner.id === currentUser?.id) && (
                                      <Box 
                                        component="span" 
                                        title="Your Team"
                                        sx={{
                                          fontSize: '0.7rem',
                                          bgcolor: 'primary.main',
                                          color: 'primary.contrastText',
                                          px: 0.75,
                                          py: 0.25,
                                          borderRadius: 1,
                                          ml: 0.5
                                        }}
                                      >
                                        YOU
                                      </Box>
                                    )}
                                  </Box>
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {teamSeason.wins}-{teamSeason.losses}
                            {teamSeason.ties ? `-${teamSeason.ties}` : ''}
                          </TableCell>
                          <TableCell align="right">
                            {('pointsScored' in teamSeason && typeof teamSeason.pointsScored === 'number') ? teamSeason.pointsScored?.toFixed(2) : '0.00'}
                          </TableCell>
                          <TableCell align="right">
                            {('pointsAgainst' in teamSeason && typeof teamSeason.pointsAgainst === 'number') ? teamSeason.pointsAgainst.toFixed(2) : '0.00'}
                          </TableCell>
                          <TableCell align="center">
                            {teamSeason.owners?.[0] ? teamSeason.owners.map(owner =>
                              <Box sx={{ display: 'flex', alignItems: 'left', gap: 1, justifyContent: 'right' }}>
                                <Button onClick={() => setViewingUser(teamSeason.owners?.[0])}>  
                                  <Typography variant="body2">
                                    {'username' in owner ? (owner as any).username : 'Owner'}
                                  </Typography>
                                  {'picture' in owner && (owner as any).picture && (
                                    <Avatar 
                                      src={(owner as any).picture} 
                                      sx={{ width: 24, height: 24 }}
                                    />
                                  )}
                                </Button>
                              </Box>
                            ) : joinEnabled ? (
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => teamSeason.teamId && handleJoinTeam(e, teamSeason.teamId)}
                                disabled={isJoining}
                              >
                                {isJoining ? 'Joining...' : 'Join Team'}
                              </Button>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No Owner
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">
                              {waiverPriorityOrder ? waiverPriorityOrder.indexOf(teamSeason.teamId!) + 1 : ""}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default DivisionStandings;
